// src/services/authService.js
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase';
import config from '../config';
import { trackEvent } from '../analytics';

const AUTH_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const FIREBASE_TOKEN_KEY = 'firebaseToken'; 

const authService = {
  // Register a new user with Firebase and create a team
  async register(email, password, teamName) {
    try {
      console.log('Starting registration with team creation:', { email, teamName });
      
      if (!teamName) {
        throw new Error('Team name is required for registration');
      }
      
      // Create the user first
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('User created successfully:', userCredential.user);
      
      // Get Firebase token
      const firebaseToken = await userCredential.user.getIdToken();
      console.log('Firebase token obtained, length:', firebaseToken.length);
      
      // Store the Firebase token
      localStorage.setItem(FIREBASE_TOKEN_KEY, firebaseToken);
      
      // Register with backend and create team
      const response = await fetch(`${config.apiBaseUrl}/api/auth/RegisterWithTeam`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          firebaseToken,
          teamName
        })
      });

      if (!response.ok) {
        // If backend registration fails, delete the Firebase user to prevent orphaned accounts
        try {
          await userCredential.user.delete();
        } catch (deleteError) {
          console.error('Failed to clean up Firebase user after backend registration failure:', deleteError);
        }
        
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create team during registration');
      }
      
      const data = await response.json();
      
      if (data.success && data.token && data.refreshToken) {
        this.setTokens(data.token, data.refreshToken);
        trackEvent('sign_up', { method: 'email_password' });
        trackEvent('create_team', { during: 'sign_up' });
        return {
          success: true,
          teamId: data.teamId,
          teamName: data.teamName
        };
      }

      throw new Error('Failed to complete registration with team creation');
    } catch (error) {
      console.error('Registration with team creation error:', error);
      throw error;
    }
  },
  
  // Login with email/password via Firebase
  async login(email, password) {
    try {
      // First try Firebase authentication
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Get Firebase token
      const firebaseToken = await userCredential.user.getIdToken();
      
      // Store the Firebase token
      localStorage.setItem(FIREBASE_TOKEN_KEY, firebaseToken);
      
      // Exchange Firebase token for an application session
      const exchangeResult = await this.exchangeFirebaseToken(firebaseToken, email);
      const success = exchangeResult?.success ?? false;
      if (success) {
        trackEvent('login', { method: 'email_password' });
      }
      return success;
    } catch (error) {
      console.error('Login error:', error);
      
      // If Firebase fails, try legacy authentication as fallback
      try {
        const formData = new FormData();
        formData.append('username', email);
        formData.append('password', password);

        const response = await fetch(`${config.apiBaseUrl}/api/UserCredentialsValidation`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const { token, refreshToken } = await response.json();
          this.setTokens(token, refreshToken);
          trackEvent('login', { method: 'legacy' });
          return true;
        }
      } catch (legacyError) {
        console.error('Legacy login error:', legacyError);
      }
      
      return false;
    }
  },
  
  // Exchange a Firebase token for application tokens/session
  async exchangeFirebaseToken(firebaseToken, email) {
    try {
      console.log('Exchanging Firebase token with backend:', { email });
      
      const response = await fetch(`${config.apiBaseUrl}/api/auth/LoginWithFirebase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firebaseToken,
          email
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        console.error('Failed to exchange Firebase token:', response.status, data);
        return { success: false, error: data.error || 'Failed to establish session with backend.' };
      }

      if (data.token && data.refreshToken) {
        this.setTokens(data.token, data.refreshToken);
      }

      return {
        success: true,
        teamId: data.teamId ?? null,
        teamName: data.teamName ?? null,
        needsTeam: Boolean(data.needsTeam),
        role: data.role ?? null
      };
    } catch (error) {
      console.error('Error exchanging Firebase token with backend:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unexpected error' };
    }
  },

  // Logout
  async logout() {
    try {
      await signOut(auth);
      this.clearTokens();
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      this.clearTokens();
      return false;
    }
  },

  // Store tokens in localStorage
  setTokens(token, refreshToken) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },

  // Store Firebase token in localStorage
  storeFirebaseToken(token) {
    if (token) {
      localStorage.setItem(FIREBASE_TOKEN_KEY, token);
    }
  },

  // Get token from localStorage
  getToken() {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  },
  
  // Get Firebase token from localStorage
  getFirebaseToken() {
    return localStorage.getItem(FIREBASE_TOKEN_KEY);
  },

  // Get refresh token from localStorage
  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  // Clear tokens from localStorage
  clearTokens() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(FIREBASE_TOKEN_KEY);
  },

  // Check if user is authenticated
  async isAuthenticated() {
    return new Promise((resolve) => {
      // Check Firebase auth state
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          // Firebase user is authenticated
          const appToken = this.getToken();
          
          if (appToken) {
            try {
              // Validate token with backend
              const isValid = await this.validateToken(appToken);
              resolve(isValid);
            } catch (error) {
              console.error('Token validation error:', error);
              resolve(false);
            }
          } else {
            // No app token, try to get one from Firebase token
            try {
              const firebaseToken = await user.getIdToken(true); // Force refresh
              localStorage.setItem(FIREBASE_TOKEN_KEY, firebaseToken); // Store the refreshed token
              const exchange = await this.exchangeFirebaseToken(firebaseToken, user.email);
              resolve(exchange?.success ?? false);
            } catch (error) {
              console.error('Firebase token validation error:', error);
              resolve(false);
            }
          }
        } else {
          // No Firebase user
          resolve(false);
        }
      });
    });
  },

  // Validate token with backend
  async validateToken(token) {
    try {
      // This should use the app JWT token, not Firebase token
      const response = await fetch(`${config.apiBaseUrl}/api/auth/ValidateAppToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      return response.ok;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  },

  // Refresh token
  async refreshToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/auth/RefreshToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refreshToken
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.token) {
          localStorage.setItem(AUTH_TOKEN_KEY, data.token);
          if (data.refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
          }
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  },

  // Check if user has any teams
  async checkTeamMembership() {
    const token = this.getToken();
    const firebaseToken = this.getFirebaseToken();
    if (!token && !firebaseToken) return null;
    
    try {
      // First try with application JWT token
      if (token) {
        const response = await fetch(`${config.apiBaseUrl}/api/auth/GetUserDetails`, {
          method: 'GET',
          headers: {
            ...this.getAuthHeaders()
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            return {
              hasTeam: !!data.user.team_id,
              teamId: data.user.team_id,
              teamName: data.user.team_name,
              role: data.user.role
            };
          }
        }
      }
      
      // If that fails, try with Firebase token as fallback
      // This is for backward compatibility until the backend is updated
      if (firebaseToken) {
        console.log('Trying GetUserDetails with Firebase token as fallback');
        const fbResponse = await fetch(`${config.apiBaseUrl}/api/auth/GetUserDetails`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${firebaseToken}`
          }
        });
        
        if (fbResponse.ok) {
          const fbData = await fbResponse.json();
          if (fbData.success && fbData.user) {
            return {
              hasTeam: !!fbData.user.team_id,
              teamId: fbData.user.team_id,
              teamName: fbData.user.team_name,
              role: fbData.user.role
            };
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('Team membership check error:', error);
      return null;
    }
  },

  // Get auth headers for API requests
  getAuthHeaders() {
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  },
  
  // Get Firebase auth headers for specific Firebase token endpoints
  getFirebaseAuthHeaders() {
    const token = this.getFirebaseToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  },

  async redeemInviteWithFirebaseToken(inviteToken, firebaseToken) {
    if (!firebaseToken) {
      throw new Error('Missing Firebase token');
    }

    const response = await fetch(`${config.apiBaseUrl}/api/invites/${inviteToken}/redeem`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ firebaseToken })
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to redeem invitation');
    }

    this.storeFirebaseToken(firebaseToken);
    if (data.token && data.refreshToken) {
      this.setTokens(data.token, data.refreshToken);
    }

    return data;
  },

  async registerWithInvite(email, password, inviteToken) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseToken = await userCredential.user.getIdToken(true);
    const result = await this.redeemInviteWithFirebaseToken(inviteToken, firebaseToken);
    trackEvent('sign_up', { method: 'email_password_invite' });
    trackEvent('accept_invite', { during: 'sign_up' });
    return result;
  },

  async loginWithInvite(email, password, inviteToken) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseToken = await userCredential.user.getIdToken(true);
    const result = await this.redeemInviteWithFirebaseToken(inviteToken, firebaseToken);
    trackEvent('login', { method: 'email_password_invite' });
    trackEvent('accept_invite', { during: 'login' });
    return result;
  },

  async redeemInviteWithCurrentSession(inviteToken) {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user session found');
    }

    const firebaseToken = await currentUser.getIdToken(true);
    return this.redeemInviteWithFirebaseToken(inviteToken, firebaseToken);
  },

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  }
};

export default authService;
